import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import {
  createDocumentPivotTableWatcher,
  createGoogleCompanyDocumentWatcher,
  createPivotTableWatcher,
} from './pivotTable'
import types from '../actions/types'
import * as contactActions from '../actions/contacts'
import * as dialogAction from '../actions/dialog' //'viewCompanyDialogContactLoad'
import * as dataActions from '../actions/data'
import * as pivotTableActions from '../actions/pivotTable'
import {snackbarMessage} from "actions/snackbar"
import {generalError} from '../actions/index'
import {get, isEmpty, isNil} from 'lodash'
import {
  getCompanyDocumentsFolderPath,
  getFileFormConfig,
  getFileFormData,
  validateFileType,
} from 'helpers/documents'
import apiHelper, {companyInbound, contactInbound} from '../helpers/api'
import * as onboardingActions from '../actions/onboarding'
import {SKILLS} from 'helpers/onboarding'
import {
  getContactSearchSearchField,
  getSelectedCompany,
  getSelectedCompanyId,
  getSelectedContact,
  getSelectedContactCurrentDocumentFolder,
  getSelectedContactDocumentFolders,
  getSelectedContactDocumentSubFolder,
  isCompanyContactsShowDormants,
  isContactSearchShowingDormants,
  isShowAllContactsActivities,
  isShowAllContactsOpportunities,
  isShowCompletedActivities,
  isShowingClosed,
  isShowRejectedActivities,
} from 'selectors/contacts'
import {contactsContextActivitiesTabPivotTableKey} from 'reducers/contactsContext/selected/activitiesTab'
import {contactsContextQuotesTabPivotTableKey} from 'reducers/contactsContext/selected/quotesTab'
import {contactsContextsInvoicesTabPivotTableKey} from 'reducers/contactsContext/selected/invoicesTab'
import {
  CONTACT_COMPANY_SELECTOR_PAGE_SIZE,
  getCompaniesFromContacts,
  getCompanyFields,
  getContactFields,
  getSearchField,
  isSearchingAllFields,
  updateParamsWithSelectedCompanyContact,
} from 'helpers/contacts'
import {contactsContextOpportunitiesTabPivotTableKey} from 'reducers/contactsContext/selected/opportunitiesTab'
import {contactsContextDocumentsTabPivotTableKey} from 'reducers/contactsContext/selected/documentsTab'
import {getHomeCompanyId} from 'selectors/auth'
import {activitiesTabPivotTableKey} from 'reducers/quotingContext/selected/activitiesTab'
import {invoicesTabPivotTableKey} from 'reducers/quotingContext/selected/invoicesTab'
import {contactsContextGridPivotTableKey} from 'reducers/contactsContext/grid'
import {loadHomeContacts} from 'actions/data'
import {selectCompany as navSelectCompany, selectContact as navSelectContact} from 'actions/navigation'
import {opportunityFields} from 'sagas/opportunitySaga'
import {createBatches} from 'helpers/arrays'
import {deObserveCompanies, deObserveContacts, observeCompanies, observeContacts} from 'actions/socket'
import {loadAccountLicenceInfo} from 'actions/settings'
import {contactsSearchPivotTableKey} from 'reducers/contactsContext/search'
import {getSearch} from 'selectors/pivotTable'
import {contactsContextCompanyContactsPivotTableKey} from 'reducers/contactsContext/selected/companyContacts'
import {viewCompanyContactsPivotTableKey} from 'reducers/dialogs/viewCompanyDialog'
import {getViewCompanyDialogCompanyId} from 'selectors/dialog'
import {clearSearch} from 'actions/pivotTable'
import {contactsContextNewDocumentsTabPivotTableKey} from "reducers/contactsContext/selected/newDocumentsTab"
import {t} from "helpers/language"

// Maximum number of records to save at one time
const MAX_BULK_SAVE_BATCH_SIZE = 50


export function* saveContactChangesToApi(data, api) {
  yield put(contactActions.bulkSaveBegin())
  try {
    const companies = getCompaniesFromContacts(data)

    const companyBatches = createBatches(companies, MAX_BULK_SAVE_BATCH_SIZE)
    const contactBathes = createBatches(data, MAX_BULK_SAVE_BATCH_SIZE)
    const totalRecords = companyBatches.length + contactBathes.length

    let lastResponse = {}
    let currentRecord = 0

    for (let i = 0; i < companyBatches.length; i++) {
      const companies = companyBatches[i]
      lastResponse = yield call(api.company.edit, {companies})
      if (!lastResponse.ok) return lastResponse

      yield put(contactActions.bulkSaveUpdate(++currentRecord, totalRecords))
    }

    for (let i = 0; i < contactBathes.length; i++) {
      const contacts = contactBathes[i]
      lastResponse = yield call(api.contact.edit, {contacts})
      if (!lastResponse.ok) return lastResponse

      yield put(contactActions.bulkSaveUpdate(++currentRecord, totalRecords))
    }

    return lastResponse

  } finally {
    yield put(contactActions.bulkSaveFinish())
  }
}


export default (api) => {

  function* websocketConnected() {
    try {
      yield put(observeCompanies())
      yield put(observeContacts())
    } catch (e) {
      yield put(generalError(e.message))
    }
  }

  function* logoutSuccess() {
    try {
      yield put(deObserveCompanies())
      yield put(deObserveContacts())
    } catch (e) {
      yield put(generalError(e.message))
    }
  }

  function* removeRecent({companyId, contactId = null}) {
    try {
      const selected = yield select(getSelectedCompanyId)
      if (!contactId && selected === companyId) {
        const homeCompanyId = yield select(state => state.auth.user.homeCompanyId)
        yield put(navSelectCompany(homeCompanyId))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Searched for contacts or companies
  function* searchContactsAndCompanies({phrase}) {
    try {
      const mode = yield select(state => state.contactsContext.search.mode)
      if (mode === 'CONTACTS') {
        yield put(contactActions.loadContacts({search: phrase}))
      } else {
        yield put(contactActions.loadCompanies({search: phrase}))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Contact filters were updated
  function* contactFiltersUpdated({filters}) {
    try {
      const mode = yield select(state => state.contactsContext.search.mode)
      if (mode === 'CONTACTS') {
        yield put(contactActions.loadContacts({filters}))
      } else {
        yield put(contactActions.loadCompanies({filters}))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Contact list was selected
  function* contactListSelected({contactList}) {
    try {
      const mode = yield select(state => state.contactsContext.search.mode)
      if (mode === 'CONTACTS') {
        yield put(contactActions.loadContacts({contactList}))
      } else {
        yield put(contactActions.loadCompanies({contactList}))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Loads a list of contacts given optional parameters
  function* loadContacts({index, search, contactList}) {
    try {
      const state = yield select(state => state.contactsContext.search)
      if (index == null) index = 0  // always reset to top of list if not specified
      if (search == null) search = state.searched

      const shouldShowDormants = state.filters.showDormants
      if (isNil(contactList)) contactList = state.selectedList

      const params = {
        start: index,
        limit: CONTACT_COMPANY_SELECTOR_PAGE_SIZE,
        dormants: shouldShowDormants,
        sortColumn: 'forename',
        sortDirection: 'ASC',
        count: true,
      }

      if (!isEmpty(search)) params['search'] = search
      if (!isEmpty(contactList)) params['contactListId'] = contactList.id

      const fields = {
        default: true,
        company: {
          default: true,
          companyLogoImage: true,
          referralcompany: true,
          createdBy: true,
          modifiedBy: true,
        },
      }

      const response = yield call(api.contact.get, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(contactActions.loadContactsSuccess(data, index))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.loadContactsFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Loads a list of companies given parameters
  function* loadCompanies({search, index, contactList, typeId}) {
    try {
      const state = yield select(state => state.contactsContext.search)

      if (index == null) index = 0  // always reset to top of list if not specified
      if (search == null) search = state.searched
      const shouldShowDormants = state.filters.showDormants
      if (isNil(contactList)) contactList = state.selectedList // Add it to the parameters

      const params = {
        start: index,
        limit: CONTACT_COMPANY_SELECTOR_PAGE_SIZE,
        dormants: shouldShowDormants,
        sortColumn: 'name',
        sortDirection: 'ASC',
        count: true,
      }

      if (search !== null && search !== '') params['search'] = search
      if (!isEmpty(contactList)) params['contactListId'] = contactList.id

      if (!isNil(typeId)) {
        params['typeId'] = typeId
      } else {
        params['typeId'] = [0, 1]
      }

      const fields = {
        default: true,
        companyLogoImage: true,
        maincontact: {
          email: true,
          forename: true,
          surname: true,
        },
      }

      const response = yield call(api.company.get, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(contactActions.loadCompaniesSuccess(data, index))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.loadCompaniesFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Loads dependant data for the selected contact
  function* loadContact({contactId}) {
    try {
      const currentCompanyId = yield select(getSelectedCompanyId)

      const response = yield call(api.contact.getOne, contactId, getContactFields())
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            let contact = data.result
            if (contact) {
              contact = apiHelper.contactInbound(contact)
              yield put(contactActions.loadContactSuccess(contact))
              yield put(contactActions.addRecent(contact.company, contact))

              const newCompanyId = get(contact, 'companyId')
              if (newCompanyId !== currentCompanyId)
                yield put(clearSearch(contactsContextCompanyContactsPivotTableKey, true))

            } else {
              yield put(contactActions.loadContactFailure('Contact not found'))
            }
          },
          function* onFail(key, message, code) {
            yield put(contactActions.loadContactFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadMainContactForCompany({companyId}) {
    try {
      const currentCompanyId = yield select(getSelectedCompanyId)

      const params = {
        companyId,
        main: true,
        dormants: true,
      }

      const response = yield call(api.contact.get, params, getContactFields())
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const contacts = data.result
            if (isEmpty(contacts)) {
              yield put(contactActions.loadContactFailure('Main contact not found'))

            } else {
              let contact = contacts[0]
              contact = apiHelper.contactInbound(contact)
              yield put(contactActions.loadContactSuccess(contact))
              yield put(contactActions.addRecent(contact.company, contact))

              const newCompanyId = get(contact, 'companyId')
              if (newCompanyId !== currentCompanyId)
                yield put(clearSearch(contactsContextCompanyContactsPivotTableKey, true))
            }
          },
          function* onFail(key, message, code) {
            yield put(contactActions.loadContactFailure(message, code))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addContact({contact, selectContact, callback}) {
    try {
      const response = yield call(api.contact.add, {contact: apiHelper.contactOutbound(contact)}, {})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const contactData = data.result[0]
            yield put(contactActions.addContactSuccess(contactData))

            const homeCompanyId = yield select(getHomeCompanyId)
            if (contactData.company.id === homeCompanyId)
              yield put(loadHomeContacts())

            if (selectContact) {
              yield put(navSelectContact(contactData.contact.id, null, true))
            } else {
              yield put(contactActions.addRecent(contactData.company, contactData.contact))
            }

            yield put(snackbarMessage(
                [contact.forename, contact.surname, 'has been added to your database'].join(' '),
                'checkmark')
            )

            if (callback)
              callback({success: true, data: contactData})

            yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))

            const skill = yield select(state => state.onboarding.skills.ADD_CONTACT)
            if (skill.active) {
              if (skill.completed) {
                yield put(onboardingActions.finishSkill(SKILLS.ADD_CONTACT.key))
              } else {
                yield put(onboardingActions.skillPage(SKILLS.ADD_CONTACT.key, 3))
              }
            }
          },
          function* onFail(key, message, code) {
            yield put(contactActions.addContactFailure(message, code))
            if (callback) callback({success: false, message})
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* editContact({contact, callback}) {
    try {
      const response = yield call(
          api.contact.edit,
          {contact: apiHelper.contactOutbound(contact)},
          getContactFields()
      )
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            // Note: success action handled by websocket; see contactUpdated()
            if (callback) {
              const updatedContact = contactInbound(data.result[0])
              callback({success: true, data: updatedContact})
            }
          },
          function* onFail(key, message, code) {
            yield put(contactActions.editContactFailure(message, code))
            if (callback) callback({success: false, message})
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactUpdated({data}) {
    try {
      const {success, result} = data

      if (success) {
        yield put(contactActions.editContactSuccess(contactInbound(result)))
        yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))
      }

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addCompany({company, selectCompany = true, callback}) {
    try {
      const response = yield call(api.company.add, {contact: apiHelper.companyOutbound(company)}, {})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const companyData = data.result[0]
            yield put(contactActions.addCompanySuccess(companyData))

            if (selectCompany) {
              yield put(navSelectContact(companyData.contact.id, companyData.company.id))
            } else {
              yield put(contactActions.addRecent(companyData.company))
            }

            yield put(snackbarMessage(`${company.name} has been added to your database`, 'checkmark'))

            if (callback)
              callback({success: true, data: companyData})

            yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.addCompanyFailure(message, code))
            if (callback) callback({success: false, message})
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* editCompany({company, callback}) {
    try {
      const response = yield call(
          api.company.edit,
          {company: apiHelper.companyOutbound(company)},
          getCompanyFields()
      )
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            let companyData = data.result[0]
            companyData = companyInbound(companyData)
            yield put(contactActions.editCompanySuccess(companyData))
            if (callback) callback({success: true, data: companyData})
          },
          function* onFail(key, message, code) {
            yield put(contactActions.editCompanyFailure(message, code))
            if (callback) callback({success: false, message})
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Searched for existing companies
  function* loadExistingCompanies({searchPhrase}) {
    try {
      const index = 0  // always reset to top of list if not specified
      const showDormants = false
      const params = {search: searchPhrase, index, limit: 100, showDormants}
      const fields = {default: true, locations: true}

      const response = yield call(api.company.get, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(contactActions.loadExistingCompaniesSuccess(data))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.loadExistingCompaniesFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Load the company fields definition
  function* loadCompanyDataFields() {
    try {
      const response = yield call(api.fields.get, {type: 'company'}, {})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const fields = data.result
            yield put(dataActions.loadCompanyDataFieldsSuccess(fields))
          },
          function* onFail(key, message, code) {
            yield put(dataActions.loadCompanyDataFieldsFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Load the company fields definition
  function* loadContactDataFields() {
    try {
      const response = yield call(api.fields.get, {type: 'contact'}, {})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const fields = data.result
            yield put(dataActions.loadContactDataFieldsSuccess(fields))
          },
          function* onFail(key, message, code) {
            yield put(dataActions.loadContactDataFieldsFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Load the company fields definition
  function* loadIndividualDataFields() {
    try {
      const response = yield call(api.fields.get, {type: 'individual'}, {})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const fields = data.result
            yield put(dataActions.loadIndividualDataFieldsSuccess(fields))
          },
          function* onFail(key, message, code) {
            yield put(dataActions.loadIndividualDataFieldsFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  // Load the parent folder of documents from Google Drive
  function* loadCompanyDocumentsFolder({companyId}) {
    try {
      const response = yield call(api.documents.getCompanyDocumentsFolder, companyId)
      if (response.ok) {
        const data = response.data
        yield put(contactActions.loadDocumentsParentFolderSuccess(data))
        yield put(contactActions.loadDocuments(data))
        yield put(contactActions.loadDocuments(data))
      } else {
        yield put(contactActions.loadDocumentsParentFolderFailure(response.message, response.code))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadDocuments({folder}) {
    try {
      const key = contactsContextDocumentsTabPivotTableKey
      yield put(pivotTableActions.loadData(key, {}))
      const response = yield call(api.documents.getDocuments, folder.id)
      if (response.ok) {
        const rootFolder = yield select(state => state.contactsContext.selected.documentsTab.rootFolder)
        const folders = yield select(state => getSelectedContactDocumentFolders(state))

        // Check if we have opened any folders yet
        if (isEmpty(folders)) {
          yield put(contactActions.addFolderToDocuments(folder))
        } else {
          // If the folder id isn't the same as the folder we have selected add it to the list
          // else load the root directory
          if (rootFolder.id !== folder.id) {
            // Find we are navigating to previous folder remove all the folders
            // after the one we are navigating to else add the folder to the list
            if (folders.filter(function (f) {
              return f.id === folder.id
            }).length > 0) {
              const currentIndex = 1
              const index = folders.findIndex(f => f.id === folder.id) + currentIndex
              yield put(contactActions.removeFoldersAfterIndex(index))
            } else {
              yield put(contactActions.addFolderToDocuments(folder))
            }
          } else {
            yield put(contactActions.setFolderDocumentsToRootDirectory(folder))
          }
        }

        const data = response.data.data
        const count = get(data, 'length', 0)
        yield put(pivotTableActions.loadDataSuccess(key, data, count))
      } else {
        yield put(pivotTableActions.loadDataFailure(key, response.message, response.code))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* uploadDocuments({documents}) {
    try {
      yield put(snackbarMessage('Uploading your files...', 'loading', false, false))
      // Get the current selected folder to send the data to
      const currentFolder = yield select(state => getSelectedContactCurrentDocumentFolder(state))
      const response = yield call(
          api.documents.uploadDocuments,
          currentFolder.id,
          getFileFormData(documents),
          getFileFormConfig()
      )
      if (response.ok) {
        yield put(contactActions.loadDocuments(currentFolder))
        yield put(snackbarMessage('Your files have been uploaded', 'checkmark'))
      } else {
        yield put(contactActions.uploadDocumentsFailure(response.message, response.code))
        yield put(snackbarMessage('Your files could not be uploaded: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteDocuments({documentIds}) {
    try {
      yield put(snackbarMessage('Deleting your files...', 'loading', false, false))
      const response = yield call(api.documents.delete, documentIds)
      if (response.ok) {
        const currentFolder = yield select(state => getSelectedContactCurrentDocumentFolder(state))
        yield put(contactActions.loadDocuments(currentFolder))
        const failedFiles = response.data.data.failed
        if (!isEmpty(failedFiles)) {
          const failedQty = failedFiles.length
          yield put(snackbarMessage(`There was an error deleting ${failedQty} of your files`, 'error', false))
        } else {
          yield put(snackbarMessage('Your files have been deleted', 'checkmark'))
        }
      } else {
        yield put(snackbarMessage('Your files could not be deleted: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* dormantContact({contactId, replaceId}) {
    try {
      const response = yield call(api.contact.dormant, contactId, replaceId)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const result = data.result[0].contactType
            yield put(contactActions.dormantContactSuccess(result, contactId, replaceId))
            yield put(loadAccountLicenceInfo())
            yield put(dialogAction.viewCompanyDialogContactLoad(contactId))
            yield put(snackbarMessage(t('This contact has been marked as dormant'), 'checkmark'))
            yield put(contactActions.loadContact(contactId))
            yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.dormantContactFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* dormantCompany({companyId}) {
    try {
      const response = yield call(api.company.dormant, companyId)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(contactActions.dormantCompanySuccess(companyId))
            yield put(snackbarMessage(t('This company has been marked as dormant'), 'checkmark'))
            // TODO: make dormant company return a full queriable API so the company can be returned.
            yield put(contactActions.loadCompany(companyId))
            yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.dormantCompanyFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* liveContact({contactId}) {
    try {
      const response = yield call(api.contact.live, contactId)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const result = data.result[0].contactType
            yield put(contactActions.liveContactSuccess(result, contactId))
            yield put(loadAccountLicenceInfo())
            yield put(dialogAction.viewCompanyDialogContactLoad(contactId))
            yield put(snackbarMessage(t('This contact has been marked as live'), 'checkmark'))
            yield put(contactActions.loadContact(contactId))
            yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.liveContactFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* liveCompany({companyId}) {
    try {
      const response = yield call(api.company.live, companyId)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(contactActions.liveCompanySuccess(companyId))
            yield put(snackbarMessage(t('This company has been marked as live'), 'checkmark'))
            // TODO: make live company return a full queriable API so the company can be returned.
            yield put(contactActions.loadCompany(companyId))
            yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.liveCompanyFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteContact({contactId, replaceId}) {
    try {
      const response = yield call(api.contact.delete, contactId, replaceId)
      yield api.handleResponse(
          response,
          function* onOk() {
          },
          function* onFail(key, message, code) {
            yield put(contactActions.deleteContactFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
      yield put(contactActions.deleteContactFailure(e.message))
    }
  }

  function* deleteCompany({companyId, replacementId}) {
    try {
      const response = yield call(api.company.delete, companyId, replacementId)
      yield api.handleResponse(
          response,
          function* onOk() {
          },
          function* onFail(key, message, code) {
            yield put(contactActions.deleteCompanyFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
      yield put(contactActions.deleteCompanyFailure(e.message))
    }
  }

  function* addLocation({companyId, location}) {
    try {
      const params = {companyId: companyId, ...location}
      params['contactId'] = null
      const response = yield call(api.location.add, params, {})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const result = data.result
            yield put(contactActions.addLocationSuccess(result))
            yield put(snackbarMessage(t('Your new address has been added'), 'checkmark'))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.addLocationFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* editLocation({location}) {
    try {
      const params = {
        ...location,
      }
      const fields = {}
      const response = yield call(api.location.edit, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const newLocation = data.result
            yield put(contactActions.editLocationSuccess(location.id, newLocation))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.editLocationFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteLocation({locationId}) {
    try {
      const response = yield call(api.location.delete, locationId)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(contactActions.deleteLocationSuccess(locationId))
            yield put(snackbarMessage(t('This address has been deleted'), 'checkmark'))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.deleteLocationFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactHasBeenDeleted({companyId, contactId}) {
    try {
      yield put(contactActions.removeRecent(companyId, contactId))
      yield put(navSelectCompany(companyId))
      yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* companyHasBeenDeleted({companyId}) {
    try {
      const homeCompanyId = yield select(state => state.auth.user.homeCompanyId)

      yield put(contactActions.removeRecent(companyId))
      yield put(navSelectCompany(homeCompanyId))
      yield put(pivotTableActions.resetLoaded(contactsSearchPivotTableKey))

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  const allowedCompanyImageExtensions = ['png', 'jpg']

  function* uploadCompanyImage({companyId, file}) {
    try {
      // Check the file type
      if (!validateFileType(file, allowedCompanyImageExtensions)) {
        const problem = 'Only png and jpg images are supported'
        yield put(contactActions.uploadCompanyImageFailure(problem))
        yield put(snackbarMessage('The company logo could not be added: ' + problem, 'error', false))
        return
      }

      yield put(snackbarMessage('Adding company logo...', 'loading', false, false))
      const response = yield call(api.company.uploadImage, companyId, getFileFormData(file), getFileFormConfig())
      if (response.ok) {
        const data = response.data.data.result[0]
        yield put(contactActions.uploadCompanyImageSuccess(data))
        yield put(snackbarMessage('The company logo has been added', 'checkmark'))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(contactActions.uploadCompanyImageFailure(problem))
        yield put(snackbarMessage('The company logo could not be added: ' + problem, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* removeCompanyImage({companyId}) {
    try {
      const response = yield call(api.company.removeImage, companyId)
      if (response.ok) {
        yield put(contactActions.removeCompanyImageSuccess(companyId))
        yield put(snackbarMessage('This company logo has been removed', 'checkmark'))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(contactActions.removeCompanyImageFailure(problem))
        yield put(snackbarMessage('The company logo could not be removed: ' + problem, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* bulkSubscribe({listId, subscribe}) {
    try {
      const response = yield call(api.contact.subscribe, {contactListId: listId, subscribe})
      if (response.ok) {
        const affected = response.data.data[0].result
        yield put(contactActions.bulkSubscribeSuccess(affected))
        yield put(snackbarMessage(
            `${affected} contacts have been ${subscribe ? 'opted IN' : 'opted OUT'}`,
            'checkmark'
        ))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(contactActions.bulkSubscribeFailure(problem))
        yield put(snackbarMessage('Sorry, your contacts could not be updated: ' + problem, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addContactMethod({contactMethod}) {
    try {
      const response = yield call(api.contactMethods.add, {contactMethod}, {default: true})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(contactActions.addContactMethodSuccess(data.result[0]))
            yield put(snackbarMessage('Your new contact method has been added', 'checkmark'))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.addContactMethodFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* editContactMethod({contactMethod}) {
    try {
      const response = yield call(api.contactMethods.edit, {contactMethod}, {default: true})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(contactActions.editContactMethodSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.editContactMethodFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteContactMethod({id, name}) {
    try {
      const params = {contactMethodId: id}
      const response = yield call(api.contactMethods.delete, params)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(contactActions.deleteContactMethodSuccess(id, name))
            yield put(snackbarMessage('Your contact method has been deleted', 'checkmark'))
          },
          function* onFail(key, message, code) {
            yield put(contactActions.deleteContactMethodFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* saveContactGridChanges(data) {
    return yield call(saveContactChangesToApi, data, api)
  }

  const activitiesPivotTable = createPivotTableWatcher(
      api.activity,
      contactsContextActivitiesTabPivotTableKey,
      [
        types.CONTACTS_ACTIVITIES_UPDATE_FILTERS,
        types.DIARY_ADD_ACTIVITY_SUCCESS,
        types.DIARY_EDIT_ACTIVITY_SUCCESS,
        types.DIARY_DELETE_ACTIVITY_SUCCESS,
      ],
      state => {
        const params = {
          includeCompleted: isShowCompletedActivities(state),
          includeRejected: isShowRejectedActivities(state),
          showHiddenTypes: false,
        }

        const showAllContacts = isShowAllContactsActivities(state)
        return updateParamsWithSelectedCompanyContact(state, params, showAllContacts)
      }, {
        default: true,
        activitytype: true,
        activityresource: true,
        company: {
          id: true,
          name: true,
        },
        contact: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
          phone: true,
          mobile: true,
          email: true,
        },
        priority: {
          id: true,
        },
        creator: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
        },
        order: true,
        opportunity: true,
        attendees: {
          default: true,
          contact: true,
        },
        owner: {
          default: true,
          contact: true,
        },
        activityUpdates: true,
      })

  const documentPivotTable = createGoogleCompanyDocumentWatcher(
      api.documents,
      contactsContextDocumentsTabPivotTableKey,
      [],
      state => {
        return {
          folderId: get(state, 'contactsContext.selected.documentsTab.currentFolder.id'),
          companyId: getSelectedCompanyId(state),
        }
      })

  const newDocumentPivotTable = createDocumentPivotTableWatcher(
      api.document,
      contactsContextNewDocumentsTabPivotTableKey,
      [types.CONTACTS_DOCUMENTS_SET_SELECTED_SUB_FOLDER],
      state => {
        const company = getSelectedCompany(state)
        const baseFolder = getCompanyDocumentsFolderPath(company)
        const subFolder = getSelectedContactDocumentSubFolder(state) || ''
        return {
          documentProvider: "DEFAULT",
          folderPath: baseFolder + subFolder,
          fromRoot: false,
        }
      }
  )

  const opportunitiesPivotTable = createPivotTableWatcher(
      api.opportunity,
      contactsContextOpportunitiesTabPivotTableKey,
      [
        types.CONTACTS_OPPORTUNITIES_UPDATE_FILTERS,
        types.OPPORTUNITY_ADD_SUCCESS,
        types.OPPORTUNITY_UPDATE_SUCCESS,
        types.DIARY_ADD_ACTIVITY_SUCCESS,
        types.DIARY_EDIT_ACTIVITY_SUCCESS,
      ],
      state => {
        const params = {
          showClosed: isShowingClosed(state),
          summary: true,
        }

        const showAllContacts = isShowAllContactsOpportunities(state)
        return updateParamsWithSelectedCompanyContact(state, params, showAllContacts)
      },
      opportunityFields)

  const quotesPivotTable = createPivotTableWatcher(
      api.quote,
      contactsContextQuotesTabPivotTableKey,
      [types.CONTACTS_QUOTES_UPDATE_FILTERS],
      state => {
        const params = {
          showCompleted: state.contactsContext.selected.quotesTab.showCompleted,
          showInactive: state.contactsContext.selected.quotesTab.showInactive,
          allRevisions: state.contactsContext.selected.quotesTab.showAllRevisions,
        }

        return updateParamsWithSelectedCompanyContact(state, params)
      }, {
        default: true,
        summaryinfo: true,
        company: {
          id: true,
          name: true,
        },
        contact: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
          mobile: true,
          phone: true,
          email: true,
          location: true,
          accountmanager: true,
        },
        creator: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
        },
        estimator: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
        },
        salesman: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
        },
        opportunity: true,
        campaign: true,
        taxcode: true,
        orderreason: true,
        rejectreason: true,
        ordertype: true,
      })

  const invoicesPivotTable = createPivotTableWatcher(
      api.invoice,
      contactsContextsInvoicesTabPivotTableKey,
      [types.CONTACTS_INVOICES_UPDATE_FILTERS],
      state => {
        const params = {
          showPosted: state.contactsContext.selected.invoicesTab.showPosted,
        }

        return updateParamsWithSelectedCompanyContact(state, params)
      }, {
        default: true,
        company: {
          default: true,
          createdBy: true,
          modifiedBy: true,
        },
        contact: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
        },
        createdby: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
        },
        order: {
          default: true,
          estimator: true,
        },
        taxcode: true,
        values: true,
      })

  const contactGridFields = {
    default: true,
    company: {
      default: true,
      accountingcompany: true,
      locations: true,
      products: true,
      groups: true,
      referralcompany: {
        name: true,
      },
      contacts: {
        id: true,
      },
      userfields: true,
      createdBy: true,
      modifiedBy: true,
    },
    contactmethods: true,
    location: true,
    userfields: true,
    accountmanager: true,
    campaign: true,
  }

  const searchPivotTable = createPivotTableWatcher(
      api.contact,
      contactsSearchPivotTableKey,
      [types.CONTACTS_UPDATE_FILTERS],
      state => {
        const searchField = getContactSearchSearchField(state)
        const search = getSearch(state, contactsSearchPivotTableKey)

        return {
          count: true,
          dormants: isContactSearchShowingDormants(state),
          searchAllFields: isSearchingAllFields(searchField),
          searchField: getSearchField(search, searchField),
        }
      },
      contactGridFields)


  const companyContactsPivotTable = createPivotTableWatcher(
      api.contact,
      contactsContextCompanyContactsPivotTableKey,
      [types.CONTACTS_COMPANY_CONTACTS_TOGGLE_DORMANTS],
      state => {
        const selectedCompanyId = getSelectedCompanyId(state)
        const selectedContact = getSelectedContact(state)

        const companyId = isNil(selectedCompanyId)
            ? get(selectedContact, 'companyId')
            : selectedCompanyId

        const limit = isNil(companyId)
            ? 0
            : null

        return {
          companyId,
          limit,
          count: false,
          dormants: isCompanyContactsShowDormants(state),
          searchAllFields: false,
          searchField: ['f_forename', 'f_surname', 'f_phone', 'f_email'],
        }
      },
      contactGridFields)

  const viewCompanyContactsPivotTable = createPivotTableWatcher(
      api.contact,
      viewCompanyContactsPivotTableKey,
      [types.CONTACTS_COMPANY_CONTACTS_TOGGLE_DORMANTS],
      state => {
        const companyId = getViewCompanyDialogCompanyId(state)

        const limit = isNil(companyId)
            ? 0
            : null

        return {
          companyId,
          limit,
          count: false,
          dormants: isCompanyContactsShowDormants(state),
          searchAllFields: false,
          searchField: ['f_forename', 'f_surname', 'f_phone', 'f_email'],
        }
      },
      contactGridFields)

  const gridPivotTable = createPivotTableWatcher(
      api.contact,
      contactsContextGridPivotTableKey,
      [types.CONTACTGRID_UPDATE_FILTERS],
      state => ({count: true, dormants: state.contactsContext.grid.showDormants}),
      contactGridFields,
      saveContactGridChanges)

  function* contactsQuotesUpdateFilters() {
    const state = yield select(state => state)
    const filters = [
      {showCompleted: state.contactsContext.selected.quotesTab.showCompleted},
      {showInactive: state.contactsContext.selected.quotesTab.showInactive},
      {allRevisions: state.contactsContext.selected.quotesTab.showAllRevisions},
    ]
    yield put(pivotTableActions.updateFilters(contactsContextQuotesTabPivotTableKey, filters))
  }

  function* contactsQuotingActivitiesUpdateFilters() {
    const state = yield select(state => state)
    const filters = [
      {showCompleted: state.quotingContext.selected.activitiesTab.showCompleted},
      {showRejected: state.quotingContext.selected.activitiesTab.showRejected},
    ]
    yield put(pivotTableActions.updateFilters(activitiesTabPivotTableKey, filters))
  }

  function* contactsQuotingInvoicesUpdateFilters() {
    const state = yield select(state => state)
    const filters = [
      {showPosted: state.quotingContext.selected.invoicesTab.showPosted},
    ]
    yield put(pivotTableActions.updateFilters(invoicesTabPivotTableKey, filters))
  }

  function* contactsInvoicesUpdateFilters() {
    const state = yield select(state => state)
    const filters = [
      {showPosted: state.contactsContext.selected.invoicesTab.showPosted},
    ]
    yield put(pivotTableActions.updateFilters(contactsContextsInvoicesTabPivotTableKey, filters))
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.CONNECTED, websocketConnected),
      takeLatest(types.LOGOUT_SUCCESS, logoutSuccess),
      takeLatest(types.CONTACTS_SEARCH, searchContactsAndCompanies),
      takeLatest(types.CONTACTS_UPDATE_FILTERS, contactFiltersUpdated),
      takeLatest(types.CONTACTS_SET_CONTACT_LIST, contactListSelected),
      takeLatest(types.CONTACTS_LOAD_CONTACTS_ATTEMPT, loadContacts),
      takeLatest(types.CONTACTS_LOAD_COMPANIES_ATTEMPT, loadCompanies),
      takeLatest(types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT, loadContact),
      takeLatest(types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT, loadMainContactForCompany),
      takeLatest(types.CONTACTS_CONTACT_EDIT_ATTEMPT, editContact),
      takeLatest(types.CONTACTS_CONTACT_UPDATED, contactUpdated),
      takeLatest(types.CONTACTS_COMPANY_EDIT_ATTEMPT, editCompany),
      takeLatest(types.CONTACTS_CONTACT_ADD_ATTEMPT, addContact),
      takeLatest(types.CONTACTS_COMPANY_ADD_ATTEMPT, addCompany),
      takeLatest(types.CONTACTS_RECENTS_REMOVE, removeRecent),
      takeLatest(types.CONTACTS_CONTACT_DELETE_SUCCESS, contactHasBeenDeleted),
      takeLatest(types.CONTACTS_COMPANY_DELETE_SUCCESS, companyHasBeenDeleted),
      takeLatest(types.CONTACTS_LOAD_EXISTING_COMPANIES_ATTEMPT, loadExistingCompanies),
      takeLatest(types.CONTACTS_LOAD_COMPANY_DATA_FIELDS_ATTEMPT, loadCompanyDataFields),
      takeLatest(types.CONTACTS_LOAD_CONTACT_DATA_FIELDS_ATTEMPT, loadContactDataFields),
      takeLatest(types.CONTACTS_LOAD_INDIVIDUAL_DATA_FIELDS_ATTEMPT, loadIndividualDataFields),
      takeLatest(types.CONTACTS_DOCUMENTS_LOAD_PARENT_FOLDER_ATTEMPT, loadCompanyDocumentsFolder),
      takeLatest(types.CONTACTS_DOCUMENTS_LOAD_ATTEMPT, loadDocuments),
      takeLatest(types.CONTACTS_DOCUMENTS_UPLOAD_ATTEMPT, uploadDocuments),
      takeLatest(types.CONTACTS_DOCUMENTS_DELETE_ATTEMPT, deleteDocuments),
      takeLatest(types.CONTACTS_CONTACT_DORMANT_ATTEMPT, dormantContact),
      takeLatest(types.CONTACTS_COMPANY_DORMANT_ATTEMPT, dormantCompany),
      takeLatest(types.CONTACTS_CONTACT_LIVE_ATTEMPT, liveContact),
      takeLatest(types.CONTACTS_COMPANY_LIVE_ATTEMPT, liveCompany),
      takeLatest(types.CONTACTS_CONTACT_DELETE_ATTEMPT, deleteContact),
      takeLatest(types.CONTACTS_COMPANY_DELETE_ATTEMPT, deleteCompany),
      takeLatest(types.CONTACTS_LOCATION_ADD_ATTEMPT, addLocation),
      takeLatest(types.CONTACTS_LOCATION_EDIT_ATTEMPT, editLocation),
      takeLatest(types.CONTACTS_LOCATION_DELETE_ATTEMPT, deleteLocation),
      takeLatest(types.CONTACTS_UPLOAD_COMPANY_IMAGE_ATTEMPT, uploadCompanyImage),
      takeLatest(types.CONTACTS_REMOVE_COMPANY_IMAGE_ATTEMPT, removeCompanyImage),
      takeLatest(types.CONTACTS_BULK_SUBSCRIBE_ATTEMPT, bulkSubscribe),
      takeLatest(types.CONTACTS_CONTACT_METHOD_ADD_ATTEMPT, addContactMethod),
      takeLatest(types.CONTACTS_CONTACT_METHOD_EDIT_ATTEMPT, editContactMethod),
      takeLatest(types.CONTACTS_CONTACT_METHOD_DELETE_ATTEMPT, deleteContactMethod),
      takeLatest(types.CONTACTS_QUOTES_UPDATE_FILTERS, contactsQuotesUpdateFilters),
      takeLatest(types.QUOTING_ACTIVITIES_UPDATE_FILTERS, contactsQuotingActivitiesUpdateFilters),
      takeLatest(types.QUOTING_INVOICES_UPDATE_FILTERS, contactsQuotingInvoicesUpdateFilters),
      takeLatest(types.CONTACTS_INVOICES_UPDATE_FILTERS, contactsInvoicesUpdateFilters),
    ])
  }

  function* watcher() {
    yield fork(myWatcher)
    yield fork(activitiesPivotTable)
    yield fork(documentPivotTable)
    yield fork(newDocumentPivotTable)
    yield fork(opportunitiesPivotTable)
    yield fork(quotesPivotTable)
    yield fork(invoicesPivotTable)
    yield fork(searchPivotTable)
    yield fork(companyContactsPivotTable)
    yield fork(viewCompanyContactsPivotTable)
    yield fork(gridPivotTable)
  }

  return {
    watcher,
  }
}
